import { z } from "zod";

// Access requests (S3.08): someone who recognizes an organization but holds no
// invite asks to join it; an admin approves it with a role or declines it. Its own
// status machine, separate from an invitation's (D139): a request is self-raised
// and starts with no role at all, where an invitation is admin-raised and already
// carries one.

// A contract may depend only on zod and @amtana/module (D017), so the role list is
// restated here rather than imported from @amtana/access or organization-contract.
export const MEMBERSHIP_ROLES = ["admin", "member", "viewer"] as const;
export const membershipRoleSchema = z.enum(MEMBERSHIP_ROLES);
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const ACCESS_REQUEST_STATUSES = ["pending", "approved", "declined"] as const;
export const accessRequestStatusSchema = z.enum(ACCESS_REQUEST_STATUSES);
export type AccessRequestStatus = (typeof ACCESS_REQUEST_STATUSES)[number];

export const accessRequestSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  name: z.string(),
  email: z.email(),
  status: accessRequestStatusSchema,
  createdAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  /** The role an approval granted; null while pending or once declined. */
  role: membershipRoleSchema.nullable(),
});
export type AccessRequest = z.infer<typeof accessRequestSchema>;

export const requestAccessInputSchema = z.object({ organizationId: z.string() });
export type RequestAccessInput = z.infer<typeof requestAccessInputSchema>;

export const accessRequestsInputSchema = z.object({});
export const accessRequestsOutputSchema = z.object({ requests: z.array(accessRequestSchema) });
export type AccessRequestsOutput = z.infer<typeof accessRequestsOutputSchema>;

export const approveAccessRequestInputSchema = z.object({
  organizationId: z.string(),
  id: z.string(),
  role: membershipRoleSchema,
});
export type ApproveAccessRequestInput = z.infer<typeof approveAccessRequestInputSchema>;

export const declineAccessRequestInputSchema = z.object({
  organizationId: z.string(),
  id: z.string(),
});
export type DeclineAccessRequestInput = z.infer<typeof declineAccessRequestInputSchema>;

/** What a shared link's landing screen shows before anyone asks to join (S3.08):
 * read by id, since the link carries the organization's own id, never its slug. */
export const previewOrganizationInputSchema = z.object({ organizationId: z.string() });
export type PreviewOrganizationInput = z.infer<typeof previewOrganizationInputSchema>;

export const previewOrganizationOutputSchema = z.object({
  organizationId: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  /** The caller already belongs: the screen offers no "ask to join" at all. */
  alreadyMember: z.boolean(),
  /** The caller already has a pending request: the screen shows it was sent, not the form. */
  alreadyRequested: z.boolean(),
});
export type PreviewOrganizationOutput = z.infer<typeof previewOrganizationOutputSchema>;
